import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { GfgSelect } from "@/components/ui/GfgSelect";
import { GfgTextField } from "@/components/ui/GfgTextField";
import { useGroups } from "@/hooks/useGroups";
import { useRoles } from "@/hooks/useRoles";
import { useUpdateUserGroup, useUpdateUserName, useUpdateUserRole } from "@/hooks/useUsers";
import type { Organization } from "@/interfaces/organization";
import type { User } from "@/interfaces/user";
import { ROLES, type Role } from "@/lib/roles";

// GFG staff whose role can't be downgraded from this simplified view — mirrors the
// reference demo's GFG_INTERNAL_ROLES set (GFG_FINANCE is deliberately excluded there too).
const PLATFORM_ADMIN_ROLES: Role[] = [ROLES.GFG_SUPER_ADMIN, ROLES.GFG_STANDARD_ADMIN];

function statusOf(u: User): "Enabled" | "Invited" | "Archived" {
  if (u.archived) return "Archived";
  if (u.verified) return "Enabled";
  return u.clerkInvitationId ? "Invited" : "Enabled";
}

/** Splits a single "Full Name" field into the firstName/lastName the API expects. */
function splitName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim();
  const [firstName, ...rest] = trimmed.split(/\s+/);
  return { firstName: firstName || trimmed, lastName: rest.join(" ") };
}

interface OrgUserEditDialogProps {
  user: User | null;
  org: Organization;
  onClose: () => void;
}

/**
 * "Edit teammate" modal for Account → Users. Matches the reference demo's csUserModalOpen
 * (edit mode): Full Name, Role, Group, read-only Status, and the GFG-Platform-Admin role
 * lock. Status has no dropdown here — no unarchive endpoint exists anywhere in the backend
 * today, so archiving stays exclusively the Delete flow's job (see OrgUserDeleteDialog).
 */
export default function OrgUserEditDialog({ user, org, onClose }: OrgUserEditDialogProps) {
  const { roles } = useRoles();
  const { data: groups = [] } = useGroups(org.id);
  const updateName = useUpdateUserName();
  const updateRole = useUpdateUserRole();
  const updateGroup = useUpdateUserGroup();

  const orgRoles = roles.filter((r) => r.level === "org");
  const isPlatformAdmin = !!user?.gfgRole && PLATFORM_ADMIN_ROLES.includes(user.gfgRole);

  const [name, setName] = useState("");
  const [role, setRole] = useState<Role | "">("");
  const [groupId, setGroupId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  // Seed form state only when a different user is opened.
  useEffect(() => {
    if (user) {
      setName(user.name);
      setRole(user.orgRole ?? "");
      setGroupId(user.groupId ?? "");
      setError(null);
    }
  }, [user]);

  const trimmedName = name.trim();
  const nameValid = trimmedName.length > 0;
  const dirty =
    !!user &&
    (trimmedName !== user.name ||
      (!isPlatformAdmin && role !== (user.orgRole ?? "")) ||
      groupId !== (user.groupId ?? ""));

  async function handleSave() {
    if (!user) return;
    setError(null);
    setSaving(true);
    try {
      if (trimmedName !== user.name) {
        const { firstName, lastName } = splitName(trimmedName);
        await updateName.mutateAsync({ userId: user.id, firstName, lastName });
      }
      if (!isPlatformAdmin && role && role !== (user.orgRole ?? "")) {
        await updateRole.mutateAsync({ userId: user.id, role, level: "org" });
      }
      if (groupId && groupId !== (user.groupId ?? "")) {
        await updateGroup.mutateAsync({ userId: user.id, groupId });
      }
      setSavedOk(true);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Dialog
        open={!!user}
        onClose={saving ? undefined : onClose}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              p: "32px",
              width: 480,
              maxWidth: "calc(100vw - 40px)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            },
          },
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={saving}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
            border: "1px solid var(--color-smoke)",
            borderRadius: "var(--radius-btn)",
            color: "var(--color-graphite)",
            width: 36,
            height: 36,
            "&:hover": { bgcolor: "var(--color-wax)", color: "var(--color-rodeo-black)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <DialogTitle
          sx={{
            p: 0,
            fontFamily: "var(--font-crimson)",
            fontSize: "24px",
            fontWeight: 400,
            color: "var(--color-rodeo-black)",
            mb: "6px",
          }}
        >
          Edit teammate
        </DialogTitle>
        <Typography sx={{ fontSize: "14px", color: "var(--color-graphite)", mb: "20px" }}>
          Update this person's role, group or status.
        </Typography>

        {user && (
          <DialogContent sx={{ p: 0, pt: "6px", overflow: "visible" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              <GfgTextField
                label="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "var(--color-rodeo-black)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: "6px",
                  }}
                >
                  Email
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    boxSizing: "border-box",
                    p: "12px 14px",
                    border: "1px solid var(--color-smoke)",
                    borderRadius: "8px",
                    bgcolor: "var(--color-wax)",
                    fontFamily: "var(--font-lato)",
                    fontSize: "14px",
                    color: "var(--color-rodeo-black)",
                  }}
                >
                  {user.email}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: "14px" }}>
                {isPlatformAdmin ? (
                  <Box sx={{ position: "relative", flex: 1, minWidth: 150 }}>
                    <Box
                      component="span"
                      sx={{
                        position: "absolute",
                        top: "-7px",
                        left: "12px",
                        px: "6px",
                        bgcolor: "var(--color-white)",
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "1.5px",
                        textTransform: "uppercase",
                        color: "var(--color-dark-dust)",
                      }}
                    >
                      Role
                    </Box>
                    <Box
                      sx={{
                        width: "100%",
                        boxSizing: "border-box",
                        p: "12px 38px 12px 14px",
                        border: "1px solid var(--color-smoke)",
                        borderRadius: "8px",
                        bgcolor: "var(--color-wax)",
                        fontFamily: "var(--font-lato)",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "var(--color-rodeo-black)",
                      }}
                    >
                      {user.gfgRole
                        ? (roles.find((r) => r.key === user.gfgRole)?.label ?? user.gfgRole)
                        : "—"}
                    </Box>
                    <LockOutlinedIcon
                      titleAccess="Managed in GfG Admin"
                      sx={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "20px",
                        color: "var(--color-graphite)",
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ flex: 1 }}>
                    <GfgSelect
                      label="Role"
                      value={role}
                      onChange={(e) => setRole(e.target.value as Role)}
                      options={orgRoles.map((r) => ({ value: r.key, label: r.label }))}
                    />
                  </Box>
                )}

                {groups.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <GfgSelect
                      label="Group"
                      value={groupId === "" ? "" : String(groupId)}
                      onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : "")}
                      options={groups.map((g) => ({ value: String(g.id), label: g.name }))}
                    />
                  </Box>
                )}
              </Box>

              {isPlatformAdmin && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                    p: "10px 12px",
                    bgcolor: "var(--color-light-dust)",
                    borderRadius: "8px",
                  }}
                >
                  <LockOutlinedIcon
                    sx={{ fontSize: "16px", color: "var(--color-dark-dust)", mt: "1px" }}
                  />
                  <Typography
                    sx={{ fontSize: "12.5px", lineHeight: 1.4, color: "var(--color-graphite)" }}
                  >
                    GfG Platform Admins can't be downgraded here. To change this person's
                    permissions, edit them in the <strong>GfG Admin</strong> space.
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "var(--color-rodeo-black)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: "6px",
                  }}
                >
                  Status
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    boxSizing: "border-box",
                    p: "12px 14px",
                    border: "1px solid var(--color-smoke)",
                    borderRadius: "8px",
                    bgcolor: "var(--color-wax)",
                    fontFamily: "var(--font-lato)",
                    fontSize: "14px",
                    color: "var(--color-rodeo-black)",
                  }}
                >
                  {statusOf(user)}
                </Box>
              </Box>

              {error && (
                <Typography
                  sx={{
                    fontSize: "13.5px",
                    color: "var(--color-good-wine)",
                    bgcolor: "var(--color-good-wine-bg)",
                    border: "1px solid var(--color-good-wine-4)",
                    borderRadius: "8px",
                    p: "10px 12px",
                  }}
                >
                  {error}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                mt: "24px",
              }}
            >
              <Button
                onClick={onClose}
                disabled={saving}
                disableRipple
                sx={{
                  fontFamily: "var(--font-lato)",
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  color: "var(--color-rodeo-black)",
                  bgcolor: "var(--color-white)",
                  border: "1px solid var(--color-smoke)",
                  borderRadius: "6px",
                  px: "20px",
                  py: "10px",
                  "&:hover": {
                    borderColor: "var(--color-dark-dust)",
                    bgcolor: "var(--color-white)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !nameValid || !dirty}
                disableRipple
                disableElevation
                sx={{
                  fontFamily: "var(--font-lato)",
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  bgcolor: "var(--color-rodeo-black)",
                  color: "var(--color-white)",
                  borderRadius: "6px",
                  px: "20px",
                  py: "10px",
                  "&:hover": { bgcolor: "#222" },
                  "&.Mui-disabled": { opacity: 0.45, color: "var(--color-white)" },
                }}
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      <Snackbar
        open={savedOk}
        autoHideDuration={4000}
        onClose={() => setSavedOk(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSavedOk(false)} sx={{ width: "100%" }}>
          Teammate updated
        </Alert>
      </Snackbar>
    </>
  );
}
